import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, XCircle, List, WarningCircle } from '@phosphor-icons/react';
import BottomNavigation from '../components/BottomNavigation';
import LoadingOverlay from '../components/LoadingOverlay';
import { workerFetch } from '../utils/apiUtils';

// Worker URL (Consider moving to a central config or env file)
const WORKER_URL = process.env.REACT_APP_API_BASE_URL || 'https://empty-rain-2654.er-hooman.workers.dev';

// Spinner Component
const Spinner = () => (
  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-black inline-block align-middle"></div>
);

// Type definition for check results
// type CheckStatus = 'idle' | 'loading' | 'success' | 'error';
// type ErrorType = 'profile' | 'analysis' | 'status' | 'api' | null;

const RoutineRequestCheck = () => {
  const navigate = useNavigate();
  const { userId, token } = useAuth();

  // State for individual checks
  const [profileStatus, setProfileStatus] = useState('idle');
  const [analysisStatus, setAnalysisStatus] = useState('idle');
  const [routineCheckStatus, setRoutineCheckStatus] = useState('idle');

  // Combined loading and error state
  const [isLoading, setIsLoading] = useState(true);
  const [errorType, setErrorType] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // State for final action
  const [allChecksPassed, setAllChecksPassed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- API Call Functions --- 
  const fetchWithAuth = useCallback(async (endpoint, options = {}) => {
    if (!userId || !token) {
      throw new Error("User ID or auth token missing");
    }
    
    console.log(`[RoutineCheck API] Calling: ${endpoint}`);
    
    return workerFetch(endpoint, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
      },
    });
  }, [userId, token]);

  const checkProfile = useCallback(async () => {
    setProfileStatus('loading');
    try {
      const response = await fetchWithAuth('/api/users/me/profile');
      if (response.ok) {
        const data = await response.json();
        if (data.profileCompleted === true) {
          setProfileStatus('success');
          return true;
        } else {
          setErrorType('profile');
          setErrorMessage('اطلاعات پروفایل شما کامل نیست.');
          setProfileStatus('error');
          return false;
        }
      } else if (response.status === 404) {
        setErrorType('profile');
        setErrorMessage('پروفایل شما یافت نشد یا کامل نیست.');
        setProfileStatus('error');
        return false;
      } else {
        throw new Error(`Profile check failed: ${response.status}`);
      }
    } catch (err) {
      console.error("RoutineRequestCheck: Caught error in checkProfile:", err); 
      setErrorType('api');
      setErrorMessage('خطا در بررسی اطلاعات پروفایل.');
      setProfileStatus('error');
      return false;
    }
  }, [fetchWithAuth]);

  const checkAnalysis = useCallback(async () => {
    setAnalysisStatus('loading');
    try {
      const response = await fetchWithAuth('/api/analysis/latest');
      if (response.ok) {
        setAnalysisStatus('success');
        return true;
      } else if (response.status === 404) {
        setErrorType('analysis');
        setErrorMessage('هنوز آنالیز پوستی برای شما ثبت نشده است.');
        setAnalysisStatus('error');
        return false;
      } else {
        throw new Error(`Analysis check failed: ${response.status}`);
      }
    } catch (err) {
      console.error("RoutineRequestCheck: Caught error in checkAnalysis:", err);
      setErrorType('api');
      setErrorMessage('خطا در بررسی وضعیت آنالیز پوست.');
      setAnalysisStatus('error');
      return false;
    }
  }, [fetchWithAuth]);

  const checkRoutineStatus = useCallback(async () => {
    setRoutineCheckStatus('loading');
    try {
      const response = await fetchWithAuth('/api/routine/status');
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'pending' || data.status === 'completed') {
          setErrorType('status');
          setErrorMessage(`شما از قبل یک درخواست روتین ${data.status === 'pending' ? 'فعال' : 'تکمیل شده'} دارید.`);
          setRoutineCheckStatus('error');
          return false;
        } else {
          setRoutineCheckStatus('success');
          return true;
        }
      } else if (response.status === 404) {
        setRoutineCheckStatus('success');
        return true;
      } else {
        throw new Error(`Routine status check failed: ${response.status}`);
      }
    } catch (err) {
      console.error("RoutineRequestCheck: Caught error in checkRoutineStatus:", err); 
      setErrorType('api');
      setErrorMessage('خطا در بررسی وضعیت درخواست روتین قبلی.');
      setRoutineCheckStatus('error');
      return false;
    }
  }, [fetchWithAuth]);

  // --- useEffect --- 
  useEffect(() => {
    console.log("RoutineRequestCheck: useEffect triggered. Context userId:", userId);
    if (!userId) { 
      console.log("RoutineRequestCheck: useEffect early exit - userId missing from context.");
      setIsLoading(false);
      return;
    }

    console.log("RoutineRequestCheck: useEffect proceeding with checks. userId:", userId);
    setIsLoading(true);
    setErrorType(null);
    setErrorMessage('');
    setAllChecksPassed(false);

    const runAllChecks = async () => {
      console.log("RoutineRequestCheck: runAllChecks started for userId:", userId);
      const profileOk = await checkProfile();
      console.log(`RoutineRequestCheck: checkProfile result: ${profileOk}`);
      if (!profileOk) {
        setIsLoading(false);
        return;
      }

      const analysisOk = await checkAnalysis();
      console.log(`RoutineRequestCheck: checkAnalysis result: ${analysisOk}`);
      if (!analysisOk) {
        setIsLoading(false);
        return;
      }

      const routineStatusOk = await checkRoutineStatus();
      console.log(`RoutineRequestCheck: checkRoutineStatus result: ${routineStatusOk}`);
      if (!routineStatusOk) {
        setIsLoading(false);
        return;
      }

      console.log("RoutineRequestCheck: All checks passed for userId:", userId);
      setAllChecksPassed(true);
      setIsLoading(false);
    };

    runAllChecks();

    return () => {
      console.log(`RoutineRequestCheck: useEffect cleanup - Component unmounting or userId changed. Current userId: ${userId}`);
    };

  }, [userId, navigate, checkProfile, checkAnalysis, checkRoutineStatus]);

  // --- Handlers --- 
  const handleSubmitRequest = async () => {
    console.log("handleSubmitRequest: Function called! Context userId:", userId);
    if (!userId || !allChecksPassed) {
      console.error("handleSubmitRequest called but conditions not met.");
      return;
    }
    
    setIsSubmitting(true);
    setErrorMessage('');
    console.log("handleSubmitRequest: Starting submission...");

    try {
      // کاربر را به صفحه روتین هدایت می‌کنیم تا پرسشنامه را تکمیل کند
      console.log("handleSubmitRequest: Redirecting to /routine for questionnaire...");
      navigate('/routine');
      
      /* کد قبلی که به صورت کامنت نگه داشته شده */
    } catch (error) {
      console.error("handleSubmitRequest: Caught error during fetch.", error);
      setErrorMessage('خطا در ارتباط با سرور هنگام ثبت درخواست. لطفاً دوباره تلاش کنید.');
      setIsSubmitting(false);
    }
  };

  // --- Render Logic --- 
  const renderCheckItem = (label, status) => {
    let Icon = Spinner;
    let colorClass = 'text-gray-500';
    let weight = 'light';

    if (status === 'loading') {
      Icon = Spinner; 
      colorClass = 'text-blue-500';
    } else if (status === 'success') {
      Icon = CheckCircle;
      colorClass = 'text-green-500';
      weight = 'fill';
    } else if (status === 'error') {
      Icon = XCircle;
      colorClass = 'text-red-500';
      weight = 'fill';
    } else if (status === 'idle') {
      Icon = List;
      colorClass = 'text-gray-400';
    }

    return (
      <div className={`flex items-center space-x-2 rtl:space-x-reverse ${colorClass}`}>
        <Icon size={20} weight={weight} className={`flex-shrink-0 ${status === 'loading' ? 'animate-spin !border-blue-500' : ''}`}/>
        <span className={`text-sm font-medium ${status === 'error' ? 'text-red-600' : status === 'success' ? 'text-green-600' : 'text-gray-700'}`}>{label}</span>
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="text-center space-y-4 py-10">
          <Spinner />
          <p className="text-sm text-gray-600">در حال بررسی پیش‌نیازها...</p>
        </div>
      );
    }

    if (errorType) {
      let buttonAction = () => {};
      let buttonText = '';
      let Icon = WarningCircle;

      if (errorType === 'profile') { buttonAction = () => navigate('/profile'); buttonText = 'تکمیل پروفایل'; }
      if (errorType === 'analysis') { buttonAction = () => navigate('/scanpage'); buttonText = 'انجام اسکن پوست'; }
      if (errorType === 'status') { buttonAction = () => navigate('/routine'); buttonText = 'مشاهده وضعیت روتین'; }
      
      return (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center space-y-4 max-w-sm mx-auto">
          <Icon size={40} className="text-red-500 mx-auto" weight="light"/>
          <h3 className="font-bold text-red-700">امکان ثبت درخواست وجود ندارد</h3>
          <p className="text-sm text-gray-600">
            {errorMessage || 'خطای نامشخصی رخ داده است.'}
          </p>
          {buttonText && (
            <button 
              onClick={buttonAction}
              className="w-full max-w-xs mx-auto bg-black text-white py-2.5 rounded-full font-bold text-sm mt-4"
            >
              {buttonText}
            </button>
          )}
        </div>
      );
    }

    if (allChecksPassed) {
      return (
        <div className="text-center space-y-6 max-w-sm mx-auto w-full">
          <div className="space-y-3 inline-flex flex-col items-start bg-green-50 p-4 rounded-lg border border-green-200">
            {renderCheckItem('پروفایل تکمیل شده است', profileStatus)}
            {renderCheckItem('آنالیز پوست انجام شده است', analysisStatus)}
            {renderCheckItem('درخواست روتین قبلی وجود ندارد', routineCheckStatus)}
          </div>
          <p className="text-sm text-green-700 font-semibold">شما آماده ثبت درخواست روتین هستید!</p>
          <button 
            onClick={handleSubmitRequest}
            disabled={isSubmitting}
            className={`w-full bg-black text-white rounded-full py-3 text-sm font-bold mt-6 transition-opacity duration-200 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
          >
            {isSubmitting ? (
                <><Spinner /> <span className="ml-2 rtl:mr-2">در حال ثبت...</span></>
            ) : (
                'ادامه و تکمیل پرسشنامه روتین ✨'
            )}
          </button>
          {!isSubmitting && errorMessage && <p className="text-xs text-red-500 mt-2">{errorMessage}</p>} 
        </div>
      );
    }

    return <p className="text-center text-gray-500">در حال بارگذاری وضعیت...</p>;
  };

  // <<< TRY block remains around the final RETURN statement >>>
  try {
    // The main content container that will be placed inside MainLayout's <Outlet />
    return (
      <div className="flex-grow flex flex-col justify-center items-center p-4 pt-10 pb-24">
        <LoadingOverlay 
          isLoading={isLoading || isSubmitting} 
          message={
            isLoading ? 'در حال بررسی پیش‌نیازها...' :
            isSubmitting ? 'در حال ثبت درخواست...' :
            'در حال بارگذاری...'
          }
        />
         <div className="w-full max-w-md">
            {/* Top Title Section - Keep this */}
            <div className="mb-12 text-center">
              <h1 className="text-xl font-bold text-black">
                بررسی پیش‌نیازهای درخواست روتین
              </h1>
              <p className="text-sm text-gray-500 mt-2">
                {isLoading ? 'در حال بررسی وضعیت شما...' : 'وضعیت پیش‌نیازها برای ثبت درخواست:'} 
              </p>
            </div>
            
            {/* Dynamic Content based on checks - Keep this */}
            {renderContent()} 
         </div>
      </div>
    );
    // <<< Header and BottomNavigation are removed, rely on MainLayout >>>

  } catch (error) {
    console.error("!!! CRITICAL ERROR during RoutineRequestCheck component RENDER !!!", error);
    // ... (catch block remains the same)
    return (
      <div style={{ padding: '20px', color: 'red', border: '2px solid red', fontFamily: 'sans-serif', direction: 'ltr', textAlign: 'left' }}>
        <h1 style={{ color: 'darkred', marginBottom: '10px' }}>Critical Component Render Error</h1>
        <h2 style={{ color: 'darkred', marginBottom: '15px' }}>RoutineRequestCheck Failed to Render</h2>
        <p style={{ marginBottom: '5px', fontWeight: 'bold' }}>Message:</p>
        <pre style={{ background: '#ffebeb', padding: '10px', borderRadius: '4px', marginBottom: '15px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {error.message}
        </pre>
        <p style={{ marginBottom: '5px', fontWeight: 'bold' }}>Stack Trace:</p>
        <pre style={{ background: '#ffebeb', padding: '10px', borderRadius: '4px', fontSize: '0.9em', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {error.stack}
        </pre>
      </div>
    );
  }
};

export default RoutineRequestCheck; 